import React from 'react';
import ChartLoader from '@/pages/dashboard/_charts/ChartLoader';
import DonutChart from '@/pages/dashboard/_charts/DonutChart';
import colors from 'tailwindcss/colors';
import {useProjectsContext} from '@/pages/dashboard/ProjectsContext';
import _ from "lodash";

/**
 * ProjectResponseYear Component
 *
 * Displays a donut chart showing the distribution of projects by partner response status.
 * Projects are categorized based on their highest priority response type (connected > pending > declined > no invites).
 *
 * @param {Object} props - Component props
 * @param {string} [props.subtitle] - Optional subtitle for the chart
 * @returns {JSX.Element} - A donut chart or loading indicator
 */
export default function ProjectResponseYear(props) {
  const {subtitle} = props;
  const {filteredProjects, isLoading} = useProjectsContext();

  // Show loading state while projects are being fetched
  if (isLoading) {
    return (
      <ChartLoader
        title="Projects by Partner Status"
        subtitle={subtitle || 'For school year'}
      />
    );
  }

  /**
   * Count projects by their highest priority response status
   *
   * Projects are categorized in priority order:
   * 1. Connected (has at least one connect response)
   * 2. Pending (has at least one pending response, no connects)
   * 3. Declined (has at least one decline response, no connects or pendings)
   * 4. No invites (has no responses at all)
   */
  const counts = _.reduce(filteredProjects, (acc, project) => {
    if (project.numberOfQuestionsConnect) acc.connected++;
    else if (project.numberOfQuestionsPending) acc.pending++;
    else if (project.numberOfQuestionsDecline) acc.declined++;
    else acc.noInvites++;
    return acc;
  }, {connected: 0, pending: 0, declined: 0, noInvites: 0});

  // Data series for the chart (in same order as labels)
  const series = [
    counts.connected,
    counts.pending,
    counts.declined,
    counts.noInvites
  ];

  // Labels for each category in the chart
  const labels = [
    'Connected',
    'Pending',
    'Declined',
    'No invites'
  ];

  // Render the donut chart with color-coded categories
  return (
    <DonutChart
      title="Projects by Partner Status"
      subtitle={subtitle || 'For school year'}
      labels={labels}
      series={series}
      colors={[
        colors.emerald['400'],  // Green for connected (positive action)
        colors.yellow['400'],   // Yellow for pending (waiting)
        colors.red['400'],      // Red for declined (negative action)
        colors.blue['400']      // Blue for no invites (neutral/inactive)
      ]}
    />
  );
}
